import { useCallback, useEffect, useRef, useState } from "react";
import {
  FaPencilAlt,
  FaPlus,
  FaRegSave,
  FaSearch,
  FaTimes,
  FaUserAlt,
  FaUserCog,
} from "react-icons/fa";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import {
  fetchUserAssignmentRules,
  fetchUserManagementOptions,
  fetchUserManagementUsers,
  updateUserManagementUser,
} from "../services/userManagementApi";

const inputClass =
  "h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base text-[#2f343a] outline-none transition placeholder:text-[#4b5563] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)] disabled:bg-[#f3f4f6] disabled:text-[#4b5563]";

const buttonBaseClass =
  "inline-flex h-[38px] items-center justify-center gap-2 rounded-md px-4 text-base font-medium transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-70";

const modalAnimationDuration = 180;
const callAgentRole = "CC_AGENT";
const fixedStatusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const emptyUserOptions = {
  roles: [],
  statuses: [],
  contactCategories: [],
};

function safeString(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}

function displayValue(value, fallback = "-") {
  const stringValue = safeString(value).trim();

  return stringValue || fallback;
}

function optionValue(option) {
  if (option && typeof option === "object") {
    return safeString(option.value ?? option.label);
  }

  return safeString(option);
}

function optionLabel(option) {
  if (option && typeof option === "object") {
    return safeString(option.label ?? option.value);
  }

  return safeString(option);
}

function normalizeOptionList(options = []) {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.filter((option) => optionValue(option));
}

function getVisibleOptions(options, currentValue) {
  const normalizedOptions = normalizeOptionList(options);
  const normalizedValue = safeString(currentValue);
  const optionValues = new Set(normalizedOptions.map(optionValue));

  if (normalizedValue && !optionValues.has(normalizedValue)) {
    return [
      {
        value: normalizedValue,
        label: normalizedValue,
      },
      ...normalizedOptions,
    ];
  }

  return normalizedOptions;
}

function isCallAgentRole(role) {
  return safeString(role).trim().toUpperCase() === callAgentRole;
}

function normalizeContactCategories(value) {
  if (Array.isArray(value)) {
    return value
      .map(optionValue)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  const stringValue = safeString(value).trim();

  if (!stringValue) {
    return [];
  }

  if (stringValue.startsWith("[")) {
    try {
      const parsedValue = JSON.parse(stringValue);

      if (Array.isArray(parsedValue)) {
        return normalizeContactCategories(parsedValue);
      }
    } catch {
      // Fall through to comma-separated handling.
    }
  }

  return stringValue
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function selectedContactCategory(values = []) {
  return values[0] || "";
}

function mapUser(row) {
  return {
    id: row?.id,
    username: safeString(row?.username),
    phone: safeString(row?.user_phonenumber ?? row?.phone),
    role: safeString(row?.role),
    status: safeString(row?.status),
    city: safeString(row?.city),
    locality: safeString(row?.locality),
    contactCategories: normalizeContactCategories(
      row?.contact_categories ?? row?.contactCategories,
    ),
    assignmentRules: [],
  };
}

function mapAssignmentRule(rule) {
  return {
    id: rule?.id,
    campaign: safeString(rule?.campaign),
    pincode: safeString(rule?.pincode),
    contactCity: safeString(rule?.contact_city ?? rule?.contactCity),
    priority: safeString(rule?.priority || 1),
    deleted: false,
  };
}

function createAssignmentRule() {
  return {
    id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    campaign: "",
    pincode: "",
    contactCity: "",
    priority: "1",
    deleted: false,
  };
}

function persistedRuleId(ruleId) {
  const numericRuleId = Number(ruleId);

  if (Number.isInteger(numericRuleId) && numericRuleId > 0) {
    return numericRuleId;
  }

  return null;
}

function mapAssignmentRuleToPayload(rule) {
  const id = persistedRuleId(rule.id);
  const priority = Number.parseInt(rule.priority, 10);
  const payload = {
    campaign: safeString(rule.campaign).trim(),
    pincode: safeString(rule.pincode).trim(),
    contact_city: safeString(rule.contactCity).trim(),
    priority: Number.isFinite(priority) && priority > 0 ? priority : 1,
    deleted: Boolean(rule.deleted),
  };

  if (id) {
    payload.id = id;
  }

  return payload;
}

function buildUserUpdatePayload(user) {
  const rules = (user.assignmentRules || []).map(mapAssignmentRuleToPayload);
  const payload = {
    status: safeString(user.status).trim(),
    city: safeString(user.city).trim(),
    locality: safeString(user.locality).trim(),
    contact_categories: selectedContactCategory(user.contactCategories),
  };

  if (rules.length > 0) {
    payload.rules = rules;
  }

  return payload;
}

function normalizeOptionsResponse(response) {
  return {
    roles: normalizeOptionList(response?.roles),
    statuses: normalizeOptionList(response?.statuses),
    contactCategories: normalizeContactCategories(
      response?.contact_categories ?? response?.contactCategories,
    ),
  };
}

function ModalShell({
  children,
  className = "",
  maxWidth = "max-w-[89rem]",
  onClose,
}) {
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  const requestClose = () => {
    if (closeTimerRef.current) {
      return;
    }

    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(onClose, modalAnimationDuration);
  };
  const renderedChildren =
    typeof children === "function" ? children(requestClose) : children;

  return (
    <div
      className={`user-management-modal-overlay fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/50 px-4 py-9 ${isClosing ? "is-closing" : ""}`}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          requestClose();
        }
      }}
    >
      <div
        className={`user-management-modal-panel w-full ${maxWidth} ${className} overflow-hidden rounded-md bg-white shadow-2xl ${isClosing ? "is-closing" : ""}`}
        role="dialog"
        aria-modal="true"
      >
        {renderedChildren}
      </div>
    </div>
  );
}

function EditUserModal({ user, userOptions, onClose, onSave }) {
  const [formValues, setFormValues] = useState({
    ...user,
    contactCategories: user.contactCategories || [],
    assignmentRules: user.assignmentRules || [],
  });
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const contactCategoryOptions = getVisibleOptions(
    userOptions.contactCategories,
    selectedContactCategory(formValues.contactCategories),
  );
  const visibleAssignmentRules = formValues.assignmentRules.filter(
    (rule) => !rule.deleted,
  );
  const canEditRules = isCallAgentRole(formValues.role);

  const updateField = (fieldName) => (event) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [fieldName]: event.target.value,
    }));
  };

  const handleSubmit = async (event, requestClose) => {
    event.preventDefault();
    setSaveError("");
    setIsSaving(true);

    try {
      await onSave(formValues);
      requestClose();
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const addAssignmentRule = () => {
    setFormValues((currentValues) => ({
      ...currentValues,
      assignmentRules: [
        ...currentValues.assignmentRules,
        createAssignmentRule(),
      ],
    }));
  };

  const updateAssignmentRule = (ruleId, fieldName, value) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      assignmentRules: currentValues.assignmentRules.map((rule) =>
        rule.id === ruleId ? { ...rule, [fieldName]: value } : rule,
      ),
    }));
  };

  const removeAssignmentRule = (ruleId) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      assignmentRules: currentValues.assignmentRules.flatMap((rule) => {
        if (rule.id !== ruleId) {
          return [rule];
        }

        return persistedRuleId(rule.id) ? [{ ...rule, deleted: true }] : [];
      }),
    }));
  };

  return (
    <ModalShell
      className="max-h-[calc(100vh-3rem)]"
      maxWidth="max-w-[108rem]"
      onClose={onClose}
    >
      {(requestClose) => (
        <form
          className="flex max-h-[calc(100vh-3rem)] flex-col"
          onSubmit={(event) => handleSubmit(event, requestClose)}
        >
          <div className="app-scrollbar grid min-h-0 flex-1 grid-cols-[34%_66%] overflow-y-auto max-[980px]:grid-cols-1">
            <section className="border-r border-[#d9dfe8] px-10 py-8 max-[980px]:border-r-0 max-[980px]:border-b max-[980px]:px-5">
              <h2 className="mb-6 flex items-center gap-3 text-base font-bold text-[#2f343a]">
                <FaUserAlt
                  className="h-4 w-4 flex-none text-[#432071]"
                  aria-hidden="true"
                />
                User Details
              </h2>

              <div className="grid gap-4">
                <label className="block">
                  <span className="mb-1.5 block text-base text-[#2f343a]">
                    Username
                  </span>
                  <input
                    className={inputClass}
                    value={formValues.username}
                    disabled
                    readOnly
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-base text-[#2f343a]">
                    Mobile
                  </span>
                  <input
                    className={inputClass}
                    inputMode="numeric"
                    maxLength={10}
                    value={formValues.phone}
                    disabled
                    readOnly
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-base text-[#2f343a]">
                    Role
                  </span>
                  <input
                    className={inputClass}
                    value={formValues.role}
                    disabled
                    readOnly
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-base text-[#2f343a]">
                    City
                  </span>
                  <input
                    className={inputClass}
                    value={formValues.city}
                    onChange={updateField("city")}
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-base text-[#2f343a]">
                    Status
                  </span>
                  <select
                    className={inputClass}
                    value={safeString(formValues.status).trim().toLowerCase()}
                    onChange={updateField("status")}
                  >
                    {fixedStatusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-base text-[#2f343a]">
                    Contact Categories
                  </span>
                  <select
                    className={inputClass}
                    value={selectedContactCategory(
                      formValues.contactCategories,
                    )}
                    onChange={(event) =>
                      setFormValues((currentValues) => ({
                        ...currentValues,
                        contactCategories: [event.target.value],
                      }))
                    }
                  >
                    <option value="">Select category</option>
                    {contactCategoryOptions.map((category) => (
                      <option
                        key={optionValue(category)}
                        value={optionValue(category)}
                      >
                        {optionLabel(category)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-base text-[#2f343a]">
                    Locality
                  </span>
                  <input
                    className={inputClass}
                    value={formValues.locality}
                    onChange={updateField("locality")}
                  />
                </label>
              </div>
            </section>

            <section className="px-5 py-8">
              <h2 className="mb-5 flex items-center gap-3 text-base font-bold text-[#2f343a]">
                <FaUserCog
                  className="h-4 w-4 flex-none text-[#8d78be]"
                  aria-hidden="true"
                />
                Call Assignment Logic
              </h2>

              {canEditRules ? (
                <>
                  <div className="mb-5 grid gap-3">
                    {visibleAssignmentRules.map((rule) => (
                      <div
                        className="grid grid-cols-[minmax(10rem,1.3fr)_minmax(7rem,0.8fr)_minmax(10rem,1.3fr)_minmax(7rem,0.8fr)_2.625rem] items-end gap-7 border border-[#d9dfe8] px-3 py-3 max-[1280px]:grid-cols-2 max-[760px]:grid-cols-1"
                        key={rule.id}
                      >
                        <label className="block">
                          <span className="mb-1.5 block text-base text-[#2f343a]">
                            Campaign
                          </span>
                          <input
                            className={inputClass}
                            value={rule.campaign}
                            onChange={(event) =>
                              updateAssignmentRule(
                                rule.id,
                                "campaign",
                                event.target.value,
                              )
                            }
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1.5 block text-base text-[#2f343a]">
                            Pincode
                          </span>
                          <input
                            className={inputClass}
                            inputMode="numeric"
                            value={rule.pincode}
                            onChange={(event) =>
                              updateAssignmentRule(
                                rule.id,
                                "pincode",
                                event.target.value,
                              )
                            }
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1.5 block text-base text-[#2f343a]">
                            Contact City
                          </span>
                          <input
                            className={inputClass}
                            value={rule.contactCity}
                            onChange={(event) =>
                              updateAssignmentRule(
                                rule.id,
                                "contactCity",
                                event.target.value,
                              )
                            }
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1.5 block text-base text-[#2f343a]">
                            Priority
                          </span>
                          <input
                            className={inputClass}
                            inputMode="numeric"
                            value={rule.priority}
                            onChange={(event) =>
                              updateAssignmentRule(
                                rule.id,
                                "priority",
                                event.target.value,
                              )
                            }
                          />
                        </label>
                        <button
                          aria-label="Remove assignment rule"
                          className="inline-flex h-[38px] w-[32px] items-center justify-center justify-self-end rounded-md bg-[#dc3545] text-white transition hover:bg-[#bb2d3b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f3a4ad] disabled:cursor-not-allowed disabled:opacity-70 max-[1280px]:justify-self-start"
                          type="button"
                          disabled={isSaving}
                          onClick={() => removeAssignmentRule(rule.id)}
                        >
                          <FaTimes className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    ))}

                    {visibleAssignmentRules.length === 0 ? (
                      <div className="border border-dashed border-[#cbd5e1] px-3 py-4 text-base text-[#4b5563]">
                        No assignment rules found.
                      </div>
                    ) : null}
                  </div>
                  <button
                    className={`${buttonBaseClass} border border-[#0d73ff] bg-white text-[#0d73ff] hover:bg-[#eff6ff] focus-visible:ring-[#9fc8ff]`}
                    type="button"
                    disabled={isSaving}
                    onClick={addAssignmentRule}
                  >
                    <FaPlus
                      className="h-4 w-4 flex-none text-[#6b4bc3]"
                      aria-hidden="true"
                    />
                    Add Rule
                  </button>
                </>
              ) : (
                <div className="border border-dashed border-[#cbd5e1] px-3 py-4 text-base text-[#4b5563]">
                  Assignment rules are available for Call Agent users.
                </div>
              )}
            </section>
          </div>

          <div className="flex min-h-[88px] flex-none items-center justify-between gap-4 border-t border-[#d9dfe8] px-5 max-[720px]:flex-col max-[720px]:items-stretch max-[720px]:py-4">
            <button
              aria-label="Close edit user modal"
              className="inline-flex h-[38px] items-center justify-center rounded-md border border-[#d8dee6] bg-white px-4 text-base font-medium text-[#4b5563] transition hover:bg-[#f8fafc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#cbd5e1] disabled:cursor-not-allowed disabled:opacity-70"
              type="button"
              disabled={isSaving}
              onClick={requestClose}
            >
              Close
            </button>
            <div className="flex flex-wrap items-center justify-end gap-3">
              {saveError ? (
                <p
                  className="m-0 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-3 py-2 text-sm text-[#842029]"
                  role="alert"
                >
                  {saveError}
                </p>
              ) : null}
              <button
                className={`${buttonBaseClass} bg-[#1773f8] text-white hover:bg-[#0d63dc] focus-visible:ring-[#9fc8ff]`}
                type="submit"
                aria-busy={isSaving}
                disabled={isSaving}
              >
                <FaRegSave className="h-4 w-4 flex-none" aria-hidden="true" />
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [userOptions, setUserOptions] = useState(emptyUserOptions);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [usersError, setUsersError] = useState("");
  const [optionsError, setOptionsError] = useState("");
  const [editError, setEditError] = useState("");
  const [isUsersLoading, setIsUsersLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [loadingRulesUserId, setLoadingRulesUserId] = useState(null);
  const usersRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  usePageTitle("User Management | Pargro");

  const loadUsers = useCallback(async (search = "") => {
    const requestId = usersRequestIdRef.current + 1;
    usersRequestIdRef.current = requestId;

    setIsUsersLoading(true);
    setUsersError("");

    try {
      const response = await fetchUserManagementUsers({ search });

      if (!isMountedRef.current || requestId !== usersRequestIdRef.current) {
        return;
      }

      setUsers((response.users || []).map(mapUser));
    } catch (error) {
      if (!isMountedRef.current || requestId !== usersRequestIdRef.current) {
        return;
      }

      setUsers([]);
      setUsersError(error.message);
    } finally {
      if (isMountedRef.current && requestId === usersRequestIdRef.current) {
        setIsUsersLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    let shouldIgnore = false;

    async function loadOptions() {
      setOptionsError("");

      try {
        const response = await fetchUserManagementOptions();

        if (!shouldIgnore) {
          setUserOptions(normalizeOptionsResponse(response));
        }
      } catch (error) {
        if (!shouldIgnore) {
          setUserOptions(emptyUserOptions);
          setOptionsError(error.message);
        }
      }
    }

    loadOptions();

    return () => {
      shouldIgnore = true;
    };
  }, []);

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const nextSearchTerm = searchTerm.trim();
    setAppliedSearchTerm(nextSearchTerm);
    loadUsers(nextSearchTerm);
  };

  const handleSearchReset = (event) => {
    event.preventDefault();
    setSearchTerm("");
    setAppliedSearchTerm("");
    loadUsers("");
  };

  const handleOpenEditUser = async (user) => {
    setEditError("");
    setLoadingRulesUserId(user.id);

    try {
      const rules = isCallAgentRole(user.role)
        ? await fetchUserAssignmentRules(user.id)
        : [];

      setEditingUser({
        ...user,
        assignmentRules: rules.map(mapAssignmentRule),
      });
    } catch (error) {
      setEditError(error.message);
    } finally {
      setLoadingRulesUserId(null);
    }
  };

  const handleSaveUser = async (updatedUser) => {
    await updateUserManagementUser(
      updatedUser.id,
      buildUserUpdatePayload(updatedUser),
    );

    setUsers((currentUsers) =>
      currentUsers.map((user) =>
        user.id === updatedUser.id
          ? {
              ...user,
              phone: updatedUser.phone,
              role: updatedUser.role,
              status: updatedUser.status,
              city: updatedUser.city,
              locality: updatedUser.locality,
              contactCategories: updatedUser.contactCategories,
              assignmentRules: updatedUser.assignmentRules,
            }
          : user,
      ),
    );
    loadUsers(appliedSearchTerm);
  };

  return (
    <ActionPageLayout
      title="User Management"
      icon={
        <FaUserCog
          className="h-6 w-6 flex-none text-[#6d4b8a]"
          aria-hidden="true"
        />
      }
    >
      <section className="mt-8">
        <h2 className="mb-3 text-[2rem] font-bold tracking-normal text-[#111827] max-[720px]:text-2xl">
          User Management Panel
        </h2>

        {optionsError ? (
          <div
            className="mb-4 rounded-md border border-[#fff3cd] bg-[#fff8e1] px-4 py-3 text-sm text-[#5b3b00]"
            role="alert"
          >
            {optionsError}
          </div>
        ) : null}

        {editError ? (
          <div
            className="mb-4 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
            role="alert"
          >
            {editError}
          </div>
        ) : null}

        <form
          className="mb-8 grid grid-cols-[minmax(18rem,50rem)_auto_auto_1fr] items-center gap-2 max-[980px]:grid-cols-1"
          onSubmit={handleSearchSubmit}
          onReset={handleSearchReset}
        >
          <input
            className={inputClass}
            placeholder="Search by username or phone"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <button
            className={`${buttonBaseClass} bg-[#1773f8] text-white hover:bg-[#0d63dc] focus-visible:ring-[#9fc8ff]`}
            type="submit"
            disabled={isUsersLoading}
          >
            <FaSearch className="h-4 w-4 flex-none" aria-hidden="true" />
            {isUsersLoading ? "Loading..." : "Search"}
          </button>
          <button
            className={`${buttonBaseClass} bg-[#6c757d] text-white hover:bg-[#5c656d] focus-visible:ring-[#b8c0c7]`}
            type="reset"
            disabled={isUsersLoading && !appliedSearchTerm}
          >
            Reset
          </button>
          <span aria-hidden="true" />
        </form>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[80rem] border-collapse text-left text-base">
            <thead>
              <tr>
                {[
                  "ID",
                  "Username",
                  "Phone",
                  "Role",
                  "Contact Categories",
                  "Status",
                  "City",
                  "Actions",
                ].map((header) => (
                  <th
                    className="border border-[#d9dfe8] bg-white px-3 py-3 font-bold text-black"
                    key={header}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isUsersLoading ? (
                <tr>
                  <td
                    className="border border-[#d9dfe8] bg-white px-3 py-6 text-center text-base text-[#4b5563]"
                    colSpan={8}
                  >
                    Loading users...
                  </td>
                </tr>
              ) : usersError ? (
                <tr>
                  <td
                    className="border border-[#d9dfe8] bg-white px-3 py-6 text-center text-base text-[#842029]"
                    colSpan={8}
                  >
                    {usersError}
                  </td>
                </tr>
              ) : users.length > 0 ? (
                users.map((user) => {
                  const isRulesLoading = loadingRulesUserId === user.id;
                  const isActionDisabled = Boolean(loadingRulesUserId);

                  return (
                    <tr
                      className="odd:bg-[#f0f0f0] even:bg-white"
                      key={user.id}
                    >
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        {user.id}
                      </td>
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        {displayValue(user.username)}
                      </td>
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        {displayValue(user.phone)}
                      </td>
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        {displayValue(user.role)}
                      </td>
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        {user.contactCategories.length
                          ? user.contactCategories.join(", ")
                          : "-"}
                      </td>
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        {displayValue(user.status)}
                      </td>
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        {displayValue(user.city)}
                      </td>
                      <td className="border border-[#d9dfe8] px-3 py-2.5">
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            className="inline-flex h-[38px] items-center justify-center gap-2 rounded-md bg-[#ffc107] px-3 text-base font-medium text-[#111827] transition hover:bg-[#f0b400] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd968] disabled:cursor-not-allowed disabled:opacity-70"
                            type="button"
                            aria-busy={isRulesLoading}
                            disabled={isActionDisabled}
                            onClick={() => handleOpenEditUser(user)}
                          >
                            <FaPencilAlt
                              className="h-4 w-4 flex-none text-[#e65ba8]"
                              aria-hidden="true"
                            />
                            {isRulesLoading ? "Loading..." : "Edit"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    className="border border-[#d9dfe8] bg-white px-3 py-6 text-center text-base text-[#4b5563]"
                    colSpan={8}
                  >
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          userOptions={userOptions}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveUser}
        />
      )}
    </ActionPageLayout>
  );
}

export default UserManagementPage;
